import React from 'react';
import { rematerializeFile, EXPIRED_FILE_MESSAGE } from './fileHealth';
import { MAX_IMAGES_PER_ORDER } from './orderLimits';

// Volume-based pricing function - rate based on TOTAL square inches (quantity included)
function calculateVolumePricing(widthInches, heightInches, quantity) {
  const perPrintSquareInches = widthInches * heightInches;
  const totalSquareInches = perPrintSquareInches * quantity;
  
  // Volume pricing tiers based on TOTAL square inches - Fixed tier boundaries to eliminate gaps
  let rate = 0;
  
  if (totalSquareInches >= 0 && totalSquareInches <= 10) {
    rate = 0.35;
  } else if (totalSquareInches > 10 && totalSquareInches <= 20) {
    rate = 0.25;
  } else if (totalSquareInches > 20 && totalSquareInches <= 50) {
    rate = 0.15;
  } else if (totalSquareInches > 50 && totalSquareInches <= 100) {
    rate = 0.1;
  } else if (totalSquareInches > 100 && totalSquareInches <= 200) {
    rate = 0.08;
  } else if (totalSquareInches > 200 && totalSquareInches <= 400) {
    rate = 0.07;
  } else if (totalSquareInches > 400 && totalSquareInches <= 600) {
    rate = 0.06;
  } else if (totalSquareInches > 600 && totalSquareInches <= 800) {
    rate = 0.05;
  } else if (totalSquareInches > 800 && totalSquareInches <= 1000) {
    rate = 0.04;
  } else if (totalSquareInches > 1000 && totalSquareInches <= 1500) {
    rate = 0.03;
  } else if (totalSquareInches > 1500 && totalSquareInches <= 2500) {
    rate = 0.025;
  } else if (totalSquareInches > 2500) {
    rate = 0.02;
  }
  
  const totalPrice = totalSquareInches * rate;
  
  return {
    perPrintSquareInches,
    totalSquareInches,
    rate,
    totalPrice,
    quantity
  };
}

// Dynamic weight calculation based on total square inches
function calculateDynamicWeight(totalSquareInches) {
  // Base weight per square inch (in pounds)
  const weightPerSquareInch = 0.0025; // ~0.04 oz per square inch
  
  // Minimum weight (envelope + protective backing)
  const minimumWeight = 0.25; // 4 oz minimum
  
  // Calculate material weight
  const materialWeight = totalSquareInches * weightPerSquareInch;
  
  // Add packaging weight (scales with order size)
  let packagingWeight = 0.125; // Base 2 oz packaging
  if (totalSquareInches > 100) {
    packagingWeight = 0.25; // 4 oz for larger orders
  }
  if (totalSquareInches > 500) {
    packagingWeight = 0.5; // 8 oz for very large orders
  }
  
  const totalWeight = materialWeight + packagingWeight;
  
  // Return the higher of calculated weight or minimum weight
  return Math.max(totalWeight, minimumWeight);
}

// Enhanced responsive hook for ShoppingCartPreview component
const useResponsive = () => {
  const [screenSize, setScreenSize] = React.useState(() => {
    if (typeof window !== 'undefined') {
      const width = window.innerWidth;
      return {
        width,
        isMobile: width < 768,
        isTablet: width >= 768 && width < 1024,
        isDesktop: width >= 1024,
        isSmallMobile: width < 480,
        isLargeMobile: width >= 480 && width < 768,
        isSmallScreen: width < 1024,
      };
    }
    return {
      width: 0,
      isMobile: false,
      isTablet: false,
      isDesktop: false,
      isSmallMobile: false,
      isLargeMobile: false,
      isSmallScreen: false,
    };
  });

  React.useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth;
      setScreenSize({
        width,
        isMobile: width < 768,
        isTablet: width >= 768 && width < 1024,
        isDesktop: width >= 1024,
        isSmallMobile: width < 480,
        isLargeMobile: width >= 480 && width < 768,
        isSmallScreen: width < 1024,
      });
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return screenSize;
};

const ShoppingCartPreview = ({ 
  cartItems, 
  removeCartItem, 
  updateCartItem, 
  onCheckout,
  isBiz,
  // Cart item resize props
  onCartItemResize,
  onRemoveWhiteBackground,
  onRemoveBlackBackground,
  CartItemResizer,
  API_BASE_URL,
  setIsResizing,
  // New props for buttons
  fileInputRef,
  loadingCart,
  // Drag and drop props
  isCartDragging,
  onCartDragEnter,
  onCartDragLeave,
  onCartDragOver,
  onCartDrop
}) => {
  const { isMobile, isTablet, isSmallMobile, isSmallScreen } = useResponsive();
  // An order can only carry so many images (see orderLimits.js). The
  // page-level error banner sits above the cart and is easily off-screen from
  // here, so the tile the customer is actually clicking has to say it too.
  const atLimit = cartItems.length >= MAX_IMAGES_PER_ORDER;
  const [quantityInputs, setQuantityInputs] = React.useState({});
  const [processingWhiteRemoval, setProcessingWhiteRemoval] = React.useState({});
  const [processingBlackRemoval, setProcessingBlackRemoval] = React.useState({});

  // POST to /process-image, preferring the server-side file reference over
  // uploading bytes — the large upload is what fails on phones. If the
  // server's copy expired (e.g. backend redeploy), retry once with the actual
  // file — except for resized items, whose local file is only a 600px preview
  // that must never be processed as if it were print-quality.
  const postProcessImage = async (item, removeField) => {
    const cd = item.customData || {};
    const sourcePath = cd.isResized ? cd.resizedImageTempPath : cd.processedImageTempPath;

    const buildForm = async (withBytes) => {
      const formData = new FormData();
      if (withBytes) {
        // Read into memory first — a dead blob (iOS evicts saved file data)
        // would truncate the upload mid-send and fail server-side.
        const freshFile = await rematerializeFile(item.file);
        if (!freshFile) return null;
        formData.append('image', freshFile);
      }
      formData.append('fileName', item.fileName);
      formData.append(removeField, 'true');
      if (sourcePath) formData.append('sourceImageTempPath', sourcePath);
      // Older backends read only this field (resized items)
      if (cd.resizedImageTempPath) {
        formData.append('resizedImageTempPath', cd.resizedImageTempPath);
      }
      return formData;
    };

    let formData = await buildForm(!sourcePath);
    if (!formData) {
      alert(EXPIRED_FILE_MESSAGE(item.fileName));
      return null;
    }
    let response = await fetch(`${API_BASE_URL}/process-image`, { method: 'POST', body: formData });

    if (!response.ok && sourcePath) {
      let code = null;
      let errText = '';
      try {
        const d = await response.clone().json();
        code = d.code;
        errText = d.error || '';
      } catch (e) { /* non-JSON error body */ }
      const serverCopyGone = response.status === 409 && code === 'TEMP_FILE_MISSING';
      const oldBackend = response.status === 400 && errText.includes('No image file provided');

      if (serverCopyGone && cd.isResized) {
        alert(`The resized image "${item.fileName}" expired on our server. Please remove it from the cart, upload the original again, and redo the resize.`);
        return null;
      }
      if (serverCopyGone || oldBackend) {
        console.log('🔁 Server copy unavailable — retrying with direct upload');
        formData = await buildForm(true);
        if (!formData) {
          alert(EXPIRED_FILE_MESSAGE(item.fileName));
          return null;
        }
        response = await fetch(`${API_BASE_URL}/process-image`, { method: 'POST', body: formData });
      }
    }
    return response;
  };

  // Handle remove white background for a cart item
  const handleRemoveWhite = async (item, index) => {
    if (!item.file || item.customData?.whiteRemoved) return;

    setProcessingWhiteRemoval(prev => ({ ...prev, [item.cartItemId]: true }));

    try {
      const response = await postProcessImage(item, 'removeWhite');
      if (!response) return;

      if (response.ok) {
        const result = await response.json();
        if (result.success && result.imageDataUrl) {
          // Call the callback to update the cart item
          if (onRemoveWhiteBackground) {
            await onRemoveWhiteBackground(item.cartItemId, result);
          }
          console.log('✅ White background removed successfully');
        } else {
          console.error('❌ White removal failed:', result.error);
          alert('Failed to remove white. Please try again.');
        }
      } else {
        console.error('❌ White removal request failed');
        alert('Failed to remove white. Please try again.');
      }
    } catch (error) {
      console.error('❌ White removal error:', error);
      alert('Failed to remove white. Please try again.');
    } finally {
      setProcessingWhiteRemoval(prev => ({ ...prev, [item.cartItemId]: false }));
    }
  };

  // Handle remove black background for a cart item
  const handleRemoveBlack = async (item, index) => {
    if (!item.file || item.customData?.blackRemoved) return;

    setProcessingBlackRemoval(prev => ({ ...prev, [item.cartItemId]: true }));

    try {
      const response = await postProcessImage(item, 'removeBlack');
      if (!response) return;

      if (response.ok) {
        const result = await response.json();
        if (result.success && result.imageDataUrl) {
          if (onRemoveBlackBackground) {
            await onRemoveBlackBackground(item.cartItemId, result);
          }
          console.log('✅ Black background removed successfully');
        } else {
          console.error('❌ Black removal failed:', result.error);
          alert('Failed to remove black. Please try again.');
        }
      } else {
        console.error('❌ Black removal request failed');
        alert('Failed to remove black. Please try again.');
      }
    } catch (error) {
      console.error('❌ Black removal error:', error);
      alert('Failed to remove black. Please try again.');
    } finally {
      setProcessingBlackRemoval(prev => ({ ...prev, [item.cartItemId]: false }));
    }
  };

  // Initialize quantity inputs when cart items change
  React.useEffect(() => {
    const newInputs = {};
    cartItems.forEach((item, index) => {
      newInputs[index] = item.quantity.toString();
    });
    setQuantityInputs(newInputs);
  }, [cartItems]);

  if (cartItems.length === 0) {
    return null; // Don't show anything if cart is empty
  }

  const calculateTotal = () => {
    // Simply sum the cart item prices (they are already correctly repriced by repriceCartItems function)
    return cartItems.reduce((total, item) => total + item.price, 0);
  };

  const totalCartQty = cartItems.reduce((total, item) => total + item.quantity, 0);
  const cartCheckoutLabel = isBiz
    ? `Proceed to Order Form (${totalCartQty} print${totalCartQty !== 1 ? 's' : ''})`
    : loadingCart
      ? 'Processing...'
      : `Checkout Cart (${totalCartQty} print${totalCartQty !== 1 ? 's' : ''})`;

  return (
    <div style={{
      width: '100%',
      maxWidth: '1200px',
      margin: '0 auto',
      // Give the cart its own breathing room as a section
      marginTop: '1.5rem',
      marginBottom: isSmallMobile ? '4rem' : isMobile ? '5rem' : '6rem',
      padding: isSmallMobile ? '1rem' : isMobile ? '1.5rem' : '2rem',
      // Use consistent 32px bottom padding
      paddingBottom: '2rem',
      background: '#ffffff',
      border: '1px solid #e2e8f0',
      borderRadius: isSmallMobile ? 12 : isMobile ? 16 : isTablet ? 20 : 24
    }}>
      {/* Header */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '1.5rem',
        paddingBottom: '1rem',
        borderBottom: '1px solid #e2e8f0'
      }}>
        <h3 style={{
          margin: 0,
          fontSize: isSmallMobile ? '1.25rem' : '1.5rem',
          fontWeight: '700',
          color: '#111827',
          display: 'flex',
          alignItems: 'center',
          gap: '0.5rem'
        }}>
          🛒 Shopping Cart ({cartItems.length})
        </h3>
        <div style={{
          display: 'grid',
          gridTemplateColumns: isSmallMobile ? '1fr 1fr' : 'auto auto auto auto',
          gap: isSmallMobile ? '1rem' : '1.5rem',
          alignItems: 'center',
          justifyItems: isSmallMobile ? 'start' : 'center'
        }}>
          {/* Total Square Inches */}
          <div style={{
            textAlign: isSmallMobile ? 'left' : 'center'
          }}>
            <div style={{
              fontSize: isSmallMobile ? '0.75rem' : '0.875rem',
              color: '#6b7280',
              fontWeight: '500',
              marginBottom: '0.25rem'
            }}>
              Total Sq In
            </div>
            <div style={{
              fontSize: isSmallMobile ? '0.9rem' : '1rem',
              fontWeight: '600',
              color: '#374151'
            }}>
              {(() => {
                const totalSqIn = cartItems.reduce((total, item) => {
                  if (item.customData?.printWidth && item.customData?.printHeight) {
                    return total + calculateVolumePricing(item.customData.printWidth, item.customData.printHeight, item.quantity).totalSquareInches;
                  }
                  return total;
                }, 0);
                return Math.ceil(totalSqIn);
              })()} sq in
            </div>
          </div>


          
          {/* Total Price */}
          <div style={{
            textAlign: isSmallMobile ? 'right' : 'center',
            gridColumn: isSmallMobile ? 'span 2' : 'auto',
            justifySelf: isSmallMobile ? 'end' : 'center'
          }}>
            <span style={{
              fontSize: isSmallMobile ? '1.25rem' : '1.5rem',
              fontWeight: '700',
              color: '#059669'
            }}>
              ${(calculateTotal() / 100).toFixed(2)}
            </span>
          </div>
        </div>
      </div>

      {/* Info Notice */}
      <div style={{
        background: 'linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%)',
        border: '2px solid #3b82f6',
        borderRadius: isSmallMobile ? 8 : 10,
        padding: isSmallMobile ? '0.75rem 1rem' : '1rem 1.25rem',
        marginBottom: '1.5rem',
        display: 'flex',
        alignItems: 'flex-start',
        gap: '0.75rem'
      }}>
        <div style={{ fontSize: '1.5rem', flexShrink: 0 }}>💾</div>
        <div>
          <div style={{
            fontSize: isSmallMobile ? '0.875rem' : '0.95rem',
            fontWeight: '600',
            color: '#1e40af',
            marginBottom: '0.25rem'
          }}>
            Cart saved automatically
          </div>
          <div style={{
            fontSize: isSmallMobile ? '0.75rem' : '0.85rem',
            color: '#1e3a8a',
            lineHeight: '1.4'
          }}>
            Your images and customizations are saved in your browser for 24 hours. 
            You can safely refresh or close this tab - your cart will be restored when you return.
          </div>
        </div>
      </div>

      {/* Cart Items - One Per Row */}
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        gap: isSmallMobile ? '1rem' : '1.25rem',
        marginBottom: '2rem'
      }}>
        {cartItems.map((item, index) => (
          <div key={item.cartItemId || index} id={`cart-item-${item.cartItemId}`} style={{
            border: '1px solid #e2e8f0',
            borderRadius: isSmallMobile ? 12 : isMobile ? 16 : isTablet ? 20 : 24,
            padding: isSmallMobile ? '1.25rem' : '1.5rem',
            background: '#fafbfc',
            transition: 'all 0.2s ease',
            width: '100%'
          }}>
            {/* Item Content - stacks vertically on mobile so the fixed-width
                image + non-shrinkable details row can't overflow the viewport */}
            <div style={{
              display: 'flex',
              flexDirection: isMobile ? 'column' : 'row',
              gap: isSmallMobile ? '1rem' : '1.5rem',
              alignItems: 'flex-start'
            }}>
              {/* Image Preview - with transparency checkerboard pattern */}
              {/* Container adapts to image aspect ratio, not fixed square */}
              <div style={{
                maxWidth: isMobile ? '100%' : '280px',
                maxHeight: isMobile ? '200px' : '280px',
                overflow: 'hidden',
                border: '1px solid #e2e8f0',
                flexShrink: 0,
                display: 'inline-block',
                // Transparency checkerboard pattern (like Photoshop)
                background: `
                  linear-gradient(45deg, #e0e0e0 25%, transparent 25%),
                  linear-gradient(-45deg, #e0e0e0 25%, transparent 25%),
                  linear-gradient(45deg, transparent 75%, #e0e0e0 75%),
                  linear-gradient(-45deg, transparent 75%, #e0e0e0 75%)
                `,
                backgroundSize: '16px 16px',
                backgroundPosition: '0 0, 0 8px, 8px -8px, -8px 0px',
                backgroundColor: '#ffffff'
              }}>
                <img
                  src={item.imagePreview}
                  alt="Cart item"
                  style={{
                    display: 'block',
                    maxWidth: '100%',
                    maxHeight: isMobile ? '200px' : '280px',
                    width: 'auto',
                    height: 'auto',
                    imageRendering: 'auto'
                  }}
                />
              </div>

                                {/* Item Details - More Compact */}
                  <div style={{
                    flex: 1,
                    // minWidth: 0 lets this column shrink below its content's
                    // intrinsic width (long filenames, quantity controls) so it
                    // can't push the row wider than the viewport
                    minWidth: 0,
                    width: isMobile ? '100%' : 'auto',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '0.75rem'
                  }}>
                {/* Header row with filename and remove button */}
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'flex-start',
                  gap: '1rem'
                }}>
                  <h4 style={{
                    margin: 0,
                    fontSize: isSmallMobile ? '1.125rem' : '1.25rem',
                    fontWeight: '600',
                    color: '#111827',
                    lineHeight: '1.3',
                    flex: 1,
                    minWidth: 0,
                    overflowWrap: 'anywhere'
                  }}>
                    {item.fileName}
                  </h4>
                  
                  {/* Remove Button */}
                  <button
                    onClick={() => removeCartItem(index)}
                    style={{
                      background: '#fef2f2',
                      border: '1px solid #fecaca',
                      borderRadius: '8px',
                      minWidth: '40px',
                      minHeight: '40px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      cursor: 'pointer',
                      color: '#dc2626',
                      flexShrink: 0,
                      fontSize: '1rem'
                    }}
                    title="Remove item"
                  >
                    🗑️
                  </button>
                </div>

                {/* Specifications Grid - More Compact */}
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: isSmallMobile ? '1fr' : 'repeat(2, 1fr)',
                  gap: isSmallMobile ? '0.5rem' : '0.75rem',
                  fontSize: isSmallMobile ? '0.8rem' : '0.85rem',
                  color: '#6b7280',
                  background: '#f8fafc',
                  padding: '0.75rem',
                  borderRadius: '6px',
                  border: '1px solid #e2e8f0'
                }}>
                  <div style={{ lineHeight: '1.3' }}>
                    <span style={{ fontWeight: '600', color: '#374151', fontSize: '0.75rem' }}>Print Size:</span><br />
                    <span style={{ fontSize: '0.85rem' }}>
                      {item.customData?.printWidth && item.customData?.printHeight 
                        ? `${item.customData.printWidth.toFixed(2)}" × ${item.customData.printHeight.toFixed(2)}"`
                        : item.printSize
                      }
                    </span>
                  </div>
                  <div style={{ lineHeight: '1.3' }}>
                    <span style={{ fontWeight: '600', color: '#374151', fontSize: '0.75rem' }}>Image Size:</span><br />
                    <span style={{ fontSize: '0.85rem' }}>{item.dimensions}</span>
                  </div>
                  {item.customData?.dpi && (
                    <div style={{ lineHeight: '1.3' }}>
                      <span style={{ fontWeight: '600', color: '#374151', fontSize: '0.75rem' }}>DPI:</span><br />
                      <span style={{ fontSize: '0.85rem' }}>{Number(item.customData.dpi)}</span>
                    </div>
                  )}
                  <div style={{ lineHeight: '1.3' }}>
                    <span style={{ fontWeight: '600', color: '#374151', fontSize: '0.75rem' }}>Total Sq In:</span><br />
                    <span style={{ fontSize: '0.85rem' }}>
                      {item.customData?.printWidth && item.customData?.printHeight 
                        ? `${(item.customData.printWidth * item.customData.printHeight * item.quantity).toFixed(2)} sq in`
                        : 'N/A'
                      }
                    </span>
                  </div>

                </div>

                {/* Quantity and Pricing Row */}
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  flexWrap: isMobile ? 'wrap' : 'nowrap',
                  gap: '1rem'
                }}>
                  {/* Quantity Controls */}
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '1rem'
                  }}>
                    <span style={{
                      fontSize: isSmallMobile ? '1rem' : '1.125rem',
                      fontWeight: '600',
                      color: '#374151'
                    }}>
                      Quantity:
                    </span>
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem',
                      background: '#ffffff',
                      borderRadius: '8px',
                      padding: '0.5rem',
                      border: '1px solid #e2e8f0'
                    }}>
                      <button
                        onClick={() => updateCartItem(index, Math.max(1, item.quantity - 1))}
                        style={{
                          background: '#f8fafc',
                          border: '1px solid #e2e8f0',
                          borderRadius: '6px',
                          minWidth: '36px',
                          minHeight: '36px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          cursor: 'pointer',
                          fontSize: '1.1rem',
                          color: '#374151',
                          fontWeight: '600'
                        }}
                      >
                        −
                      </button>
                      <input
                        type="number"
                        value={quantityInputs[index] || ''}
                        onChange={(e) => {
                          const inputValue = e.target.value;
                          setQuantityInputs(prev => ({
                            ...prev,
                            [index]: inputValue === '' || (!isNaN(parseInt(inputValue)) && parseInt(inputValue) >= 1 && parseInt(inputValue) <= 999) ? inputValue : prev[index]
                          }));
                        }}
                        onBlur={(e) => {
                          const value = parseInt(e.target.value) || 1;
                          const finalQuantity = Math.max(1, Math.min(999, value));
                          updateCartItem(index, finalQuantity);
                          setQuantityInputs(prev => ({
                            ...prev,
                            [index]: finalQuantity.toString()
                          }));
                        }}
                        min="1"
                        max="999"
                        style={{
                          width: '60px',
                          textAlign: 'center',
                          fontSize: isSmallMobile ? '1rem' : '1.125rem',
                          fontWeight: '600',
                          border: 'none',
                          background: 'transparent',
                          outline: 'none',
                          WebkitAppearance: 'none',
                          MozAppearance: 'textfield'
                        }}
                      />
                      <button
                        onClick={() => updateCartItem(index, Math.min(999, item.quantity + 1))}
                        style={{
                          background: '#f8fafc',
                          border: '1px solid #e2e8f0',
                          borderRadius: '6px',
                          minWidth: '36px',
                          minHeight: '36px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          cursor: 'pointer',
                          fontSize: '1.1rem',
                          color: '#374151',
                          fontWeight: '600'
                        }}
                      >
                        +
                      </button>
                    </div>
                  </div>

                  {/* Pricing */}
                  <div style={{
                    textAlign: isSmallMobile ? 'left' : 'right'
                  }}>
                    <div style={{
                      fontSize: isSmallMobile ? '1.25rem' : '1.375rem',
                      fontWeight: '700',
                      color: '#10b981',
                      marginBottom: '0.25rem'
                    }}>
                      ${(() => {
                        // Calculate the per-unit price based on volume pricing for entire order
                        const totalSqIn = cartItems.reduce((total, cartItem) => {
                          if (cartItem.customData?.printWidth && cartItem.customData?.printHeight) {
                            return total + (cartItem.customData.printWidth * cartItem.customData.printHeight * cartItem.quantity);
                          }
                          return total;
                        }, 0);
                        
                        let tierRate = 0;
                        if (totalSqIn > 0) {
                          if (totalSqIn >= 0 && totalSqIn <= 10) {
                            tierRate = 0.35;
                          } else if (totalSqIn > 10 && totalSqIn <= 20) {
                            tierRate = 0.25;
                          } else if (totalSqIn > 20 && totalSqIn <= 50) {
                            tierRate = 0.15;
                          } else if (totalSqIn > 50 && totalSqIn <= 100) {
                            tierRate = 0.1;
                          } else if (totalSqIn > 100 && totalSqIn <= 200) {
                            tierRate = 0.08;
                          } else if (totalSqIn > 200 && totalSqIn <= 400) {
                            tierRate = 0.07;
                          } else if (totalSqIn > 400 && totalSqIn <= 600) {
                            tierRate = 0.06;
                          } else if (totalSqIn > 600 && totalSqIn <= 800) {
                            tierRate = 0.05;
                          } else if (totalSqIn > 800 && totalSqIn <= 1000) {
                            tierRate = 0.04;
                          } else if (totalSqIn > 1000 && totalSqIn <= 1500) {
                            tierRate = 0.03;
                          } else if (totalSqIn > 1500 && totalSqIn <= 2500) {
                            tierRate = 0.025;
                          } else if (totalSqIn > 2500) {
                            tierRate = 0.02;
                          }
                        }
                        
                        const width = parseFloat(item.customData?.printWidth);
                        const height = parseFloat(item.customData?.printHeight);
                        const quantity = parseInt(item.quantity, 10) || 0;
                        
                        // Only calculate if dimensions are valid
                        if (!isNaN(width) && !isNaN(height) && width > 0 && height > 0 && quantity > 0 && tierRate > 0) {
                          const itemSqIn = width * height * quantity;
                          const itemTotalPrice = itemSqIn * tierRate;
                          const pricePerUnit = itemTotalPrice / quantity;
                          return pricePerUnit.toFixed(2);
                        }
                        // Fallback: use item.price if available, otherwise 0
                        return item.price ? (item.price / 100 / quantity).toFixed(2) : '0.00';
                      })()} each
                    </div>
                                             {item.quantity > 1 && (
                           <div style={{
                             fontSize: isSmallMobile ? '1rem' : '1.125rem',
                             color: '#6b7280',
                             fontWeight: '500'
                           }}>
                             Sub Total: ${(() => {
                               // Calculate this item's portion of the volume pricing
                               const totalSqIn = cartItems.reduce((total, cartItem) => {
                                 if (cartItem.customData?.printWidth && cartItem.customData?.printHeight) {
                                   return total + (cartItem.customData.printWidth * cartItem.customData.printHeight * cartItem.quantity);
                                 }
                                 return total;
                               }, 0);
                               
                               let tierRate = 0;
                               if (totalSqIn > 0) {
                                 if (totalSqIn >= 0 && totalSqIn <= 10) {
                                   tierRate = 0.35;
                                 } else if (totalSqIn > 10 && totalSqIn <= 20) {
                                   tierRate = 0.25;
                                 } else if (totalSqIn > 20 && totalSqIn <= 50) {
                                   tierRate = 0.15;
                                 } else if (totalSqIn > 50 && totalSqIn <= 100) {
                                   tierRate = 0.1;
                                 } else if (totalSqIn > 100 && totalSqIn <= 200) {
                                   tierRate = 0.08;
                                 } else if (totalSqIn > 200 && totalSqIn <= 400) {
                                   tierRate = 0.07;
                                 } else if (totalSqIn > 400 && totalSqIn <= 600) {
                                   tierRate = 0.06;
                                 } else if (totalSqIn > 600 && totalSqIn <= 800) {
                                   tierRate = 0.05;
                                 } else if (totalSqIn > 800 && totalSqIn <= 1000) {
                                   tierRate = 0.04;
                                 } else if (totalSqIn > 1000 && totalSqIn <= 1500) {
                                   tierRate = 0.03;
                                 } else if (totalSqIn > 1500 && totalSqIn <= 2500) {
                                   tierRate = 0.025;
                                 } else if (totalSqIn > 2500) {
                                   tierRate = 0.02;
                                 }
                               }
                               
                               const width = parseFloat(item.customData?.printWidth);
                               const height = parseFloat(item.customData?.printHeight);
                               const quantity = parseInt(item.quantity, 10) || 0;
                               
                               // Only calculate if dimensions are valid
                               if (!isNaN(width) && !isNaN(height) && width > 0 && height > 0 && quantity > 0 && tierRate > 0) {
                                 const itemSqIn = width * height * quantity;
                                 return (itemSqIn * tierRate).toFixed(2);
                               }
                               // Fallback: use item.price if available
                               return item.price ? (item.price / 100).toFixed(2) : '0.00';
                             })()}
                           </div>
                         )}
                  </div>
                </div>
              </div>
            </div>
            
            {/* Cart Item Resize Controls */}
            {CartItemResizer && item.file && (
              <CartItemResizer
                key={item.cartItemId}
                cartItem={item}
                onItemResize={onCartItemResize}
                API_BASE_URL={API_BASE_URL}
                setIsResizing={setIsResizing}
              />
            )}
            
            {/* Remove White Background Button */}
            {item.file && onRemoveWhiteBackground && !item.customData?.whiteRemoved && (
              <div style={{
                marginTop: '1rem',
                paddingTop: '1rem',
                borderTop: '1px solid #e2e8f0'
              }}>
                <div style={{ 
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.75rem',
                  flexWrap: 'wrap'
                }}>
                  <button
                    onClick={() => handleRemoveWhite(item, index)}
                    disabled={processingWhiteRemoval[item.cartItemId]}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '0.5rem',
                      padding: '0.625rem 1rem',
                      minWidth: '230px',
                      fontSize: '0.875rem',
                      fontWeight: '600',
                      color: '#dc2626',
                      background: 'linear-gradient(135deg, #fef2f2 0%, #fecaca 100%)',
                      border: '1px solid #f87171',
                      borderRadius: '8px',
                      cursor: processingWhiteRemoval[item.cartItemId] ? 'not-allowed' : 'pointer',
                      opacity: processingWhiteRemoval[item.cartItemId] ? 0.7 : 1,
                      transition: 'all 0.2s ease'
                    }}
                  >
                    {processingWhiteRemoval[item.cartItemId] ? (
                      <>
                        <svg 
                          width="16" 
                          height="16" 
                          viewBox="0 0 24 24" 
                          fill="none" 
                          stroke="currentColor" 
                          strokeWidth="2"
                          style={{ animation: 'spin 1s linear infinite' }}
                        >
                          <circle cx="12" cy="12" r="10" strokeOpacity="0.25" />
                          <path d="M12 2a10 10 0 0 1 10 10" strokeLinecap="round" />
                        </svg>
                        Processing...
                      </>
                    ) : (
                      <>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                          <line x1="9" y1="9" x2="15" y2="15" />
                          <line x1="15" y1="9" x2="9" y2="15" />
                        </svg>
                        Remove White
                      </>
                    )}
                  </button>
                  
                  <span style={{
                    fontSize: '0.75rem',
                    color: '#6b7280',
                    fontStyle: 'italic'
                  }}>
                    (Converts white pixels to transparent and trims edges)
                  </span>
                </div>
                
                {/* CSS for spinner animation */}
                <style>{`
                  @keyframes spin {
                    from { transform: rotate(0deg); }
                    to { transform: rotate(360deg); }
                  }
                `}</style>
              </div>
            )}
            
            {/* Show "White Removed" status if already processed */}
            {item.customData?.whiteRemoved && (
              <div style={{
                marginTop: '1rem',
                paddingTop: '1rem',
                borderTop: '1px solid #e2e8f0',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                color: '#059669',
                fontSize: '0.875rem',
                fontWeight: '600'
              }}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <polyline points="20 6 9 17 4 12" />
                </svg>
                White removed & trimmed
              </div>
            )}

            {/* Remove Black Background Button */}
            {item.file && onRemoveBlackBackground && !item.customData?.blackRemoved && (
              <div style={{
                marginTop: '1rem',
                paddingTop: '1rem',
                borderTop: '1px solid #e2e8f0'
              }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.75rem',
                  flexWrap: 'wrap'
                }}>
                  <button
                    onClick={() => handleRemoveBlack(item, index)}
                    disabled={processingBlackRemoval[item.cartItemId]}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '0.5rem',
                      padding: '0.625rem 1rem',
                      minWidth: '230px',
                      fontSize: '0.875rem',
                      fontWeight: '600',
                      color: '#1f2937',
                      background: 'linear-gradient(135deg, #f3f4f6 0%, #d1d5db 100%)',
                      border: '1px solid #6b7280',
                      borderRadius: '8px',
                      cursor: processingBlackRemoval[item.cartItemId] ? 'not-allowed' : 'pointer',
                      opacity: processingBlackRemoval[item.cartItemId] ? 0.7 : 1,
                      transition: 'all 0.2s ease'
                    }}
                  >
                    {processingBlackRemoval[item.cartItemId] ? (
                      <>
                        <svg
                          width="16"
                          height="16"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          style={{ animation: 'spin 1s linear infinite' }}
                        >
                          <circle cx="12" cy="12" r="10" strokeOpacity="0.25" />
                          <path d="M12 2a10 10 0 0 1 10 10" strokeLinecap="round" />
                        </svg>
                        Processing...
                      </>
                    ) : (
                      <>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                          <line x1="9" y1="9" x2="15" y2="15" />
                          <line x1="15" y1="9" x2="9" y2="15" />
                        </svg>
                        Remove Black
                      </>
                    )}
                  </button>

                  <span style={{
                    fontSize: '0.75rem',
                    color: '#6b7280',
                    fontStyle: 'italic'
                  }}>
                    (Converts black pixels to transparent and trims edges)
                  </span>
                </div>
              </div>
            )}

            {/* Show "Black Removed" status if already processed */}
            {item.customData?.blackRemoved && (
              <div style={{
                marginTop: '1rem',
                paddingTop: '1rem',
                borderTop: '1px solid #e2e8f0',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                color: '#059669',
                fontSize: '0.875rem',
                fontWeight: '600'
              }}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <polyline points="20 6 9 17 4 12" />
                </svg>
                Black removed & trimmed
              </div>
            )}
          </div>
        ))}
      </div>
      
      {/* Action Buttons - Add New Image and Checkout */}
      <div style={{
        display: 'flex',
        gap: '1rem',
        justifyContent: 'center',
        alignItems: 'stretch',
        marginTop: '2rem',
        paddingTop: '1.5rem',
        borderTop: '1px solid #e2e8f0',
        flexDirection: isSmallMobile ? 'column' : 'row'
      }}>
        {/* Add New Image Button with Drag & Drop */}
        <div
          onClick={() => {
            if (atLimit) return;
            if (fileInputRef && fileInputRef.current) {
              fileInputRef.current.click();
            }
          }}
          onDragEnter={atLimit ? undefined : onCartDragEnter}
          onDragLeave={atLimit ? undefined : onCartDragLeave}
          onDragOver={atLimit ? undefined : onCartDragOver}
          onDrop={atLimit ? undefined : onCartDrop}
          style={{
            width: '100%',
            background: atLimit ? '#fef2f2' : isCartDragging ? '#f0fdf4' : '#fafafa',
            color: atLimit ? '#dc2626' : isCartDragging ? '#10b981' : '#374151',
            fontWeight: '600',
            fontSize: isMobile ? '1rem' : '1.1rem',
            border: atLimit
              ? '3px dashed #fecaca'
              : isCartDragging ? '3px dashed #10b981' : '3px dashed #d1d5db',
            borderRadius: '12px',
            padding: isMobile ? '1.25rem 1.5rem' : '1.5rem 2rem',
            minHeight: isCartDragging ? '120px' : '80px',
            cursor: atLimit ? 'not-allowed' : 'pointer',
            opacity: 1,
            transition: 'all 0.2s ease',
            boxShadow: isCartDragging ? '0 8px 32px rgba(16, 185, 129, 0.15)' : '0 2px 8px rgba(0, 0, 0, 0.05)',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '0.75rem',
            transform: isCartDragging ? 'scale(1.02)' : 'scale(1)',
            position: 'relative',
            overflow: 'hidden'
          }}
          onMouseEnter={(e) => {
            if (!isCartDragging && !atLimit) {
              e.currentTarget.style.border = '3px dashed #10b981';
              e.currentTarget.style.background = '#f0fdf4';
              e.currentTarget.style.color = '#10b981';
            }
          }}
          onMouseLeave={(e) => {
            if (!isCartDragging && !atLimit) {
              e.currentTarget.style.border = '3px dashed #d1d5db';
              e.currentTarget.style.background = '#fafafa';
              e.currentTarget.style.color = '#374151';
            }
          }}
        >
          {/* Animated background effect when dragging */}
          {isCartDragging && (
            <div style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'radial-gradient(circle at center, rgba(16, 185, 129, 0.1) 0%, transparent 70%)',
              animation: 'pulse 2s ease-in-out infinite',
              pointerEvents: 'none'
            }} />
          )}
          
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.75rem',
            zIndex: 1
          }}>
            {atLimit ? (
              <>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
                </svg>
                <span>{MAX_IMAGES_PER_ORDER}-image limit reached</span>
              </>
            ) : isCartDragging ? (
              <>
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                  <polyline points="7,10 12,15 17,10"/>
                  <line x1="12" y1="15" x2="12" y2="3"/>
                </svg>
                <span style={{ fontSize: isMobile ? '1.1rem' : '1.2rem' }}>Drop Image Here!</span>
              </>
            ) : (
              <>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm5 11h-4v4h-2v-4H7v-2h4V7h2v4h4v2z"/>
                </svg>
                <span>Add New Image</span>
              </>
            )}
          </div>
          
          {!isCartDragging && (
            <div style={{
              fontSize: isSmallMobile ? '0.75rem' : '0.85rem',
              fontWeight: '500',
              opacity: 0.9,
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              zIndex: 1
            }}>
              {atLimit ? (
                <span style={{ textAlign: 'center' }}>
                  Remove an image to add a different one, or place this order and start another
                </span>
              ) : (
                <>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                    <polyline points="17 8 12 3 7 8"/>
                    <line x1="12" y1="3" x2="12" y2="15"/>
                  </svg>
                  <span>Click or drag & drop PNG files</span>
                </>
              )}
            </div>
          )}
        </div>

        {/* Checkout Button */}
        <button
          onClick={async () => {
            if (cartItems.length > 0) {
              await onCheckout(cartItems);
            }
          }}
          disabled={loadingCart || cartItems.length === 0}
          style={{
            width: '100%',
            background: (loadingCart || cartItems.length === 0) ? '#9ca3af' : 'linear-gradient(135deg, #635bff 0%, #5a52e8 100%)',
            color: '#ffffff',
            fontWeight: '600',
            fontSize: isMobile ? '1rem' : '1.1rem',
            border: 'none',
            borderRadius: '12px',
            padding: isMobile ? '1.25rem 1.5rem' : '1.5rem 2rem',
            minHeight: isMobile ? 'unset' : '80px',
            cursor: (loadingCart || cartItems.length === 0) ? 'not-allowed' : 'pointer',
            opacity: (loadingCart || cartItems.length === 0) ? 0.7 : 1,
            transition: 'all 0.2s ease',
            boxShadow: (loadingCart || cartItems.length === 0) ? 'none' : '0 4px 15px rgba(99, 91, 255, 0.3)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '0.5rem'
          }}
          onMouseOver={(e) => {
            if (!loadingCart && cartItems.length > 0) {
              e.target.style.background = 'linear-gradient(135deg, #5a52e8 0%, #4c46d6 100%)';
              e.target.style.transform = 'translateY(-1px)';
              e.target.style.boxShadow = '0 6px 20px rgba(99, 91, 255, 0.4)';
            }
          }}
          onMouseOut={(e) => {
            if (!loadingCart && cartItems.length > 0) {
              e.target.style.background = 'linear-gradient(135deg, #635bff 0%, #5a52e8 100%)';
              e.target.style.transform = 'translateY(0)';
              e.target.style.boxShadow = '0 4px 15px rgba(99, 91, 255, 0.3)';
            }
          }}
        >
          {isBiz ? (
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M22 2L11 13M22 2L15 22l-4-9-9-4 19-7z" />
            </svg>
          ) : (
            <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
              <path d="M13.976 9.15c-2.172-.806-3.356-1.426-3.356-2.409 0-.831.683-1.305 1.901-1.305 2.227 0 4.515.858 6.09 1.631l-.89-5.494C18.252.975 15.697 0 12.165 0 9.667 0 7.589.654 6.104 1.872 4.56 3.147 3.757 4.992 3.757 7.218c0 4.039 2.467 5.76 6.476 7.219 2.585.92 3.445 1.574 3.445 2.583 0 .98-.84 1.545-2.354 1.545-1.875 0-4.965-.921-6.90-2.109l-.9 5.555C5.175 22.99 8.385 24 11.714 24c2.641 0 4.843-.624 6.328-1.813 1.664-1.305 2.525-3.236 2.525-5.732 0-4.128-2.524-5.851-6.591-7.305z"/>
            </svg>
          )}
          {cartCheckoutLabel}
        </button>
      </div>
      
    </div>
  );
};

export default ShoppingCartPreview; 